/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxContextMenu from 'devextreme/ui/context_menu';


import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxoAnimationModule } from './nested/animation';
import { DxoHideModule } from './nested/hide';
import { DxoShowModule } from './nested/show';
import { DxiItemModule } from './nested/item-dxi';
import { DxoPositionModule } from './nested/position';
import { DxoAtModule } from './nested/at';
import { DxoBoundaryOffsetModule } from './nested/boundary-offset';
import { DxoCollisionModule } from './nested/collision';
import { DxoMyModule } from './nested/my';
import { DxoOffsetModule } from './nested/offset';
import { DxoShowEventModule } from './nested/show-event';
import { DxoShowSubmenuModeModule } from './nested/show-submenu-mode';
import { DxoDelayModule } from './nested/delay';

import { DxiItemComponent } from './nested/item-dxi';




/**
 * The ContextMenu widget displays a single- or multi-level context menu. An end user invokes this menu by a right click or a long press.
 */

export class DxContextMenuComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxContextMenu;


    /**
     * Specifies the shortcut key that sets focus on the widget.
     */
    
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * A Boolean value specifying whether or not the widget changes its state when interacting with a user.
     */
    
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Configures widget visibility animations. This object contains two fields: show and hide.
     */
    
    get animation(): any {
        return this._getOption('animation');
    }
    set animation(value: any) {
        this._setOption('animation', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is closed if a user clicks outside of the context menu.
     */
    
    get closeOnOutsideClick(): boolean|
        Function {
        return this._getOption('closeOnOutsideClick');
    }
    set closeOnOutsideClick(value: boolean|
        Function) {
        this._setOption('closeOnOutsideClick', value);
    }


    /**
     * Specifies the name of the CSS class to be applied to the root menu level and all submenus.
     */
    
    get cssClass(): string {
        return this._getOption('cssClass');
    }
    set cssClass(value: string) {
        this._setOption('cssClass', value);
    }


    /**
     * A data source used to fetch data to be displayed by the widget.
     */
    
    get dataSource(): DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate>) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the name of the data source item field whose value defines whether or not the corresponding widget item is disabled.
     */
    
    get disabledExpr(): Function|
        string {
        return this._getOption('disabledExpr');
    }
    set disabledExpr(value: Function|
        string) {
        this._setOption('disabledExpr', value);
    }


    /**
     * Specifies the name of the data source item field whose value is displayed by the widget.
     */
    
    get displayExpr(): Function|
        string {
        return this._getOption('displayExpr');
    }
    set displayExpr(value: Function|
        string) {
        this._setOption('displayExpr', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether the widget can be focused using keyboard navigation.
     */
    
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Holds an array of menu items.
     */
    
    get items(): any|
        Array<any> {
        return this._getOption('items');
    }
    set items(value: any|
        Array<any>) {
        this._setOption('items', value);
    }


    /**
     * Specifies which data field contains nested items.
     */
    
    get itemsExpr(): Function|
        string {
        return this._getOption('itemsExpr');
    }
    set itemsExpr(value: Function|
        string) {
        this._setOption('itemsExpr', value);
    }


    /**
     * Specifies a custom template for an item.
     */
    
    get itemTemplate(): any {
        return this._getOption('itemTemplate');
    }
    set itemTemplate(value: any) {
        this._setOption('itemTemplate', value);
    }


    /**
     * An object defining widget positioning options.
     */
    
    get position(): DevExpress.positionConfig {
        return this._getOption('position');
    }
    set position(value: DevExpress.positionConfig) {
        this._setOption('position', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies whether or not an item becomes selected if a user clicks it.
     */
    
    get selectByClick(): boolean {
        return this._getOption('selectByClick');
    }
    set selectByClick(value: boolean) {
        this._setOption('selectByClick', value);
    }


    /**
     * Specifies the name of the data source item field whose value defines whether or not the corresponding widget items is selected.
     */
    
    get selectedExpr(): Function|
        string {
        return this._getOption('selectedExpr');
    }
    set selectedExpr(value: Function|
        string) {
        this._setOption('selectedExpr', value);
    }


    /**
     * The selected item object.
     */
    
    get selectedItem(): any {
        return this._getOption('selectedItem');
    }
    set selectedItem(value: any) {
        this._setOption('selectedItem', value);
    }


    /**
     * An array of currently selected item objects.
     */
    
    get selectedItems(): Array<any> {
        return this._getOption('selectedItems');
    }
    set selectedItems(value: Array<any>) {
        this._setOption('selectedItems', value);
    }


    /**
     * Use the selectByClick option instead.
     */
    
    get selectionByClick(): boolean {
        return this._getOption('selectionByClick');
    }
    set selectionByClick(value: boolean) {
        this._setOption('selectionByClick', value);
    }


    /**
     * Specifies the selection mode supported by the menu.
     */
    
    get selectionMode(): string {
        return this._getOption('selectionMode');
    }
    set selectionMode(value: string) {
        this._setOption('selectionMode', value);
    }


    /**
     * Specifies options for displaying the widget.
     */
    
    get showEvent(): string|
        { delay?: number, name?: string } {
        return this._getOption('showEvent');
    }
    set showEvent(value: string|
        { delay?: number, name?: string }) {
        this._setOption('showEvent', value);
    }


    /**
     * Specifies options of submenu showing and hiding.
     */
    
    get showSubmenuMode(): string|
        { delay?: number|
        { hide?: number, show?: number }, name?: string } {
        return this._getOption('showSubmenuMode');
    }
    set showSubmenuMode(value: string|
        { delay?: number|
        { hide?: number, show?: number }, name?: string }) {
        this._setOption('showSubmenuMode', value);
    }


    /**
     * Specifies the direction at which submenus are displayed.
     */
    
    get submenuDirection(): string {
        return this._getOption('submenuDirection');
    }
    set submenuDirection(value: string) {
        this._setOption('submenuDirection', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * The target element associated with the context menu.
     */
    
    get target(): Element|
        JQuery {
        return this._getOption('target');
    }
    set target(value: Element|
        JQuery) {
        this._setOption('target', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the contentReady event. Executed when the widget's content is ready. This handler may be executed multiple times during the widget's lifetime depending on the number of times its content changes.
     */
     onContentReady: EventEmitter<any>;

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the hidden event.
     */
     onHidden: EventEmitter<any>;

    /**
     * A handler for the hiding event.
     */
     onHiding: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the itemClick event.
     */
     onItemClick: EventEmitter<any>;

    /**
     * A handler for the itemContextMenu event.
     */
     onItemContextMenu: EventEmitter<any>;

    /**
     * A handler for the itemRendered event.
     */
     onItemRendered: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the positioning event.
     */
     onPositioning: EventEmitter<any>;

    /**
     * A handler for the selectionChanged event. Raised after an item is selected or unselected.
     */
     onSelectionChanged: EventEmitter<any>;

    /**
     * A handler for the showing event.
     */
     onShowing: EventEmitter<any>;

    /**
     * A handler for the shown event.
     */
     onShown: EventEmitter<any>;

    /**
     * A handler for the accessKeyChange event.
     */
     accessKeyChange: EventEmitter<string>;

    /**
     * A handler for the activeStateEnabledChange event.
     */
     activeStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the animationChange event.
     */
     animationChange: EventEmitter<any>;

    /**
     * A handler for the closeOnOutsideClickChange event.
     */
     closeOnOutsideClickChange: EventEmitter<boolean|
        Function>;

    /**
     * A handler for the cssClassChange event.
     */
     cssClassChange: EventEmitter<string>;

    /**
     * A handler for the dataSourceChange event.
     */
     dataSourceChange: EventEmitter<DevExpress.data.DataSource|
        DevExpress.data.DataSourceOptions|
        string|
        Array<string|
        DevExpress.ui.CollectionWidgetItemTemplate>>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the disabledExprChange event.
     */
     disabledExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the displayExprChange event.
     */
     displayExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
     focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the itemsChange event.
     */
     itemsChange: EventEmitter<any|
        Array<any>>;

    /**
     * A handler for the itemsExprChange event.
     */
     itemsExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the itemTemplateChange event.
     */
     itemTemplateChange: EventEmitter<any>;

    /**
     * A handler for the positionChange event.
     */
     positionChange: EventEmitter<DevExpress.positionConfig>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the selectByClickChange event.
     */
     selectByClickChange: EventEmitter<boolean>;

    /**
     * A handler for the selectedExprChange event.
     */
     selectedExprChange: EventEmitter<Function|
        string>;

    /**
     * A handler for the selectedItemChange event.
     */
     selectedItemChange: EventEmitter<any>;

    /**
     * A handler for the selectedItemsChange event.
     */
     selectedItemsChange: EventEmitter<Array<any>>;

    /**
     * A handler for the selectionByClickChange event.
     */
     selectionByClickChange: EventEmitter<boolean>;

    /**
     * A handler for the selectionModeChange event.
     */
     selectionModeChange: EventEmitter<string>;

    /**
     * A handler for the showEventChange event.
     */
     showEventChange: EventEmitter<string|
        { delay?: number, name?: string }>;

    /**
     * A handler for the showSubmenuModeChange event.
     */
     showSubmenuModeChange: EventEmitter<string|
        { delay?: number|
        { hide?: number, show?: number }, name?: string }>;

    /**
     * A handler for the submenuDirectionChange event.
     */
     submenuDirectionChange: EventEmitter<string>;

    /**
     * A handler for the tabIndexChange event.
     */
     tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the targetChange event.
     */
     targetChange: EventEmitter<Element|
        JQuery>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;




    
    get itemsChildren(): QueryList<DxiItemComponent> {
        return this._getOption('items');
    }
    set itemsChildren(value) {
        this.setChildren('items', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'hidden', emit: 'onHidden' },
            { subscribe: 'hiding', emit: 'onHiding' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'itemClick', emit: 'onItemClick' },
            { subscribe: 'itemContextMenu', emit: 'onItemContextMenu' },
            { subscribe: 'itemRendered', emit: 'onItemRendered' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'positioning', emit: 'onPositioning' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { subscribe: 'showing', emit: 'onShowing' },
            { subscribe: 'shown', emit: 'onShown' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'animationChange' },
            { emit: 'closeOnOutsideClickChange' },
            { emit: 'cssClassChange' },
            { emit: 'dataSourceChange' },
            { emit: 'disabledChange' },
            { emit: 'disabledExprChange' },
            { emit: 'displayExprChange' },
            { emit: 'elementAttrChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'itemsChange' },
            { emit: 'itemsExprChange' },
            { emit: 'itemTemplateChange' },
            { emit: 'positionChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'selectByClickChange' },
            { emit: 'selectedExprChange' },
            { emit: 'selectedItemChange' },
            { emit: 'selectedItemsChange' },
            { emit: 'selectionByClickChange' },
            { emit: 'selectionModeChange' },
            { emit: 'showEventChange' },
            { emit: 'showSubmenuModeChange' },
            { emit: 'submenuDirectionChange' },
            { emit: 'tabIndexChange' },
            { emit: 'targetChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxContextMenu(element, options);
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('items', changes);
        this.setupChanges('selectedItems', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._idh.doCheck('items');
        this._idh.doCheck('selectedItems');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-context-menu',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'accessKey': [{ type: Input },],
'activeStateEnabled': [{ type: Input },],
'animation': [{ type: Input },],
'closeOnOutsideClick': [{ type: Input },],
'cssClass': [{ type: Input },],
'dataSource': [{ type: Input },],
'disabled': [{ type: Input },],
'disabledExpr': [{ type: Input },],
'displayExpr': [{ type: Input },],
'elementAttr': [{ type: Input },],
'focusStateEnabled': [{ type: Input },],
'height': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'items': [{ type: Input },],
'itemsExpr': [{ type: Input },],
'itemTemplate': [{ type: Input },],
'position': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'selectByClick': [{ type: Input },],
'selectedExpr': [{ type: Input },],
'selectedItem': [{ type: Input },],
'selectedItems': [{ type: Input },],
'selectionByClick': [{ type: Input },],
'selectionMode': [{ type: Input },],
'showEvent': [{ type: Input },],
'showSubmenuMode': [{ type: Input },],
'submenuDirection': [{ type: Input },],
'tabIndex': [{ type: Input },],
'target': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onContentReady': [{ type: Output },],
'onDisposing': [{ type: Output },],
'onHidden': [{ type: Output },],
'onHiding': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onItemClick': [{ type: Output },],
'onItemContextMenu': [{ type: Output },],
'onItemRendered': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onPositioning': [{ type: Output },],
'onSelectionChanged': [{ type: Output },],
'onShowing': [{ type: Output },],
'onShown': [{ type: Output },],
'accessKeyChange': [{ type: Output },],
'activeStateEnabledChange': [{ type: Output },],
'animationChange': [{ type: Output },],
'closeOnOutsideClickChange': [{ type: Output },],
'cssClassChange': [{ type: Output },],
'dataSourceChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'disabledExprChange': [{ type: Output },],
'displayExprChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'focusStateEnabledChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'itemsChange': [{ type: Output },],
'itemsExprChange': [{ type: Output },],
'itemTemplateChange': [{ type: Output },],
'positionChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'selectByClickChange': [{ type: Output },],
'selectedExprChange': [{ type: Output },],
'selectedItemChange': [{ type: Output },],
'selectedItemsChange': [{ type: Output },],
'selectionByClickChange': [{ type: Output },],
'selectionModeChange': [{ type: Output },],
'showEventChange': [{ type: Output },],
'showSubmenuModeChange': [{ type: Output },],
'submenuDirectionChange': [{ type: Output },],
'tabIndexChange': [{ type: Output },],
'targetChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
'itemsChildren': [{ type: ContentChildren, args: [DxiItemComponent, ] },],
};
}


export class DxContextMenuModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxiItemModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxoShowEventModule,
    DxoShowSubmenuModeModule,
    DxoDelayModule,
    DxTemplateModule
  ],
  declarations: [
    DxContextMenuComponent
  ],
  exports: [
    DxContextMenuComponent,
    DxoAnimationModule,
    DxoHideModule,
    DxoShowModule,
    DxiItemModule,
    DxoPositionModule,
    DxoAtModule,
    DxoBoundaryOffsetModule,
    DxoCollisionModule,
    DxoMyModule,
    DxoOffsetModule,
    DxoShowEventModule,
    DxoShowSubmenuModeModule,
    DxoDelayModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
